import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Tag, Smartphone, Phone, ArrowRight, Nfc, QrCode, Home } from 'lucide-react';

export default function HowItWorksPage() {
  const steps = [
    {
      number: '01',
      icon: Smartphone,
      title: 'Tap to Register',
      description: 'Simply tap your phone on the NFC tag to open the registration page. No app needed - it works instantly with any modern smartphone.',
      image: 'https://images.pexels.com/photos/16345589/pexels-photo-16345589.jpeg'
    },
    {
      number: '02',
      icon: Tag,
      title: 'Create Pet Profile',
      description: 'Enter your contact information and your pet\'s details. Add photos, medical notes, and any special behavior tips.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b054acb6-e244-43ab-885c-9baa9ae8c181/images/87c3b50a6a93b5504f08aa5f3212c9b45ed249b9f47a7b9ce81a6351d80c7d38.png'
    },
    {
      number: '03',
      icon: Phone,
      title: 'Instant Contact',
      description: 'If someone finds your pet, they simply tap or scan the tag with their phone. They can instantly call, text, or WhatsApp you.',
      image: 'https://images.pexels.com/photos/4214919/pexels-photo-4214919.jpeg'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-20" data-testid="how-it-works-page">
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-foreground tracking-tight">
            How SaferPaws Works
          </h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
            Three simple steps to protect your furry friend. No complicated setup, no app required.
          </p>
        </section>

        {/* Steps */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="space-y-24">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isReversed = index % 2 === 1;
              
              return (
                <div 
                  key={index}
                  className={`flex flex-col ${isReversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-12 lg:gap-20`}
                  data-testid={`step-${index + 1}`}
                >
                  {/* Image */}
                  <div className="flex-1 w-full">
                    <div className="relative">
                      <img 
                        src={step.image}
                        alt={step.title}
                        className="rounded-2xl shadow-xl w-full aspect-[4/3] object-cover"
                      />
                      <div className="absolute -top-4 -left-4 w-16 h-16 bg-primary rounded-2xl flex items-center justify-center text-primary-foreground font-heading font-bold text-xl shadow-lg">
                        {step.number}
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 w-full">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <h2 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
                        {step.title}
                      </h2>
                    </div>
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Tag Design Info */}
        <section className="bg-card/40 border-y border-border py-20 mt-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-heading font-bold text-foreground mb-6">
              What&apos;s on the Tag?
            </h2>
            <p className="text-lg text-muted-foreground mb-10">
              Each SaferPaws tag includes:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-card rounded-2xl p-6 border border-border">
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Nfc className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-foreground">NFC Chip</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  Tap with any smartphone for instant access
                </p>
              </div>
              <div className="bg-card rounded-2xl p-6 border border-border">
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary/10 flex items-center justify-center">
                  <QrCode className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-foreground">QR Code</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  Backup option — scan with any camera app
                </p>
              </div>
              <div className="bg-card rounded-2xl p-6 border border-border">
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Home className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-foreground">Friendly Message</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  &ldquo;Tap phone or scan to help me home&rdquo;
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h2 className="text-3xl font-heading font-bold text-foreground">
            Ready to Protect Your Pet?
          </h2>
          <p className="mt-4 text-muted-foreground">
            Join thousands of pet parents who trust SaferPaws
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/shop">
              <Button 
                size="lg" 
                className="w-full sm:w-auto bg-primary hover:bg-primary/90"
                data-testid="hiw-buy-tag-btn"
              >
                Buy a Tag
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/activate">
              <Button 
                size="lg" 
                variant="outline"
                className="w-full sm:w-auto"
                data-testid="hiw-activate-btn"
              >
                Activate Existing Tag
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
